import type { ReactNode } from "react";

/**
 * The pressable two-tone icon tiles of the tactile design. Every part of the app has one emoji and one colour,
 * used on its home tile, its page header and its tab, so people always know where they are.
 */
export type Area =
  | "home"
  | "shop"
  | "wallet"
  | "receipt"
  | "ledger"
  | "food"
  | "fridge"
  | "goods"
  | "report"
  | "places"
  | "board"
  | "plus"
  | "me"
  | "help"
  | "admin";

export const AREA_EMOJI: Record<Area, string> = {
  home: "🏠",
  shop: "🛒",
  wallet: "💳",
  receipt: "🧾",
  ledger: "📒",
  food: "🥗",
  fridge: "🧺",
  goods: "📦",
  report: "📊",
  places: "🏪",
  board: "💬",
  plus: "⭐",
  me: "👤",
  help: "💁",
  admin: "🛠️",
};

/** Which part of the app a page address belongs to. */
export function areaOf(path: string): Area {
  if (path === "/") return "home";
  if (path.startsWith("/shopping")) return "shop";
  if (path.startsWith("/wallet")) return "wallet";
  if (path.startsWith("/ledger/new")) return "receipt";
  if (path.startsWith("/ledger")) return "ledger";
  if (path.startsWith("/meals")) return "food";
  if (path.startsWith("/fridge")) return "fridge";
  if (path.startsWith("/goods")) return "goods";
  if (path.startsWith("/report")) return "report";
  if (path.startsWith("/places")) return "places";
  if (path.startsWith("/board")) return "board";
  if (path.startsWith("/me/plans")) return "plus";
  if (path.startsWith("/me/support")) return "help";
  if (path.startsWith("/me/admin")) return "admin";
  if (path.startsWith("/me")) return "me";
  return "home";
}

/** A small raised tile with the area's emoji. */
export function Mini({ area, size = "md", children }: { area: Area; size?: "sm" | "md" | "lg"; children?: ReactNode }) {
  return (
    <span className={`mini mini-${size} t-${area}`} aria-hidden="true">
      <span className="mini-emoji">{children ?? AREA_EMOJI[area]}</span>
    </span>
  );
}
